import { useEffect, useState } from "react";
import { ChevronDown } from "lucide-react";
import ContactType from "./contact-type";
import LeadSourcePage from "../lead-source/mainCreateLead";
import { useStateValue } from "../../context/StateProvider";
import MainUserManagement from "../user-managment/main-user";
import MainTaskTemplate from "../task-template/main-template";
import MainLockboxInventory from "../lockbox-inventory/main-lockbox";
import { useLocation } from "react-router-dom";
import {
  loadBrokerageSettings,
  isSuperAdminOwner,
} from "../../utils/brokerageSettings";
import EmailLog from "./email-log";
import EmailTemplatesTab from "./email-templates-tab";
import NotificationsTab from "./notifications-tab";
import LabelsTab from "./labels-tab";
import BulkUploadTab from "./bulk-upload-tab";
import TeamDefaultsTab from "./team-defaults-tab";
import TeamLeadsTab from "./team-leads-tab";
import CoordinatorsTab from "./coordinators-tab";
import IntegrationsTab from "./integrations-tab";
import SuperAdminTab from "./super-admin-tab";
import ImpactTab from "./impact-tab";
import PortalFormsEditor from "./PortalFormsEditor";
import { isImpactScorecardEnabled, isLeadTrackerEnabled } from "../../utils/featureFlags";
import PageHeader from "../PageHeader";

// Old top-level tabs that now live as sub-tabs under Integrations. Deep links
// (e.g. the contacts page's ?tab=gmailConnection) keep working via this map.
const LEGACY_SETTINGS_TAB_MAP = {
  gmailConnection: "gmail",
  followUpBoss: "followUpBoss",
  bombBomb: "bombBomb",
};

function MainSettings() {
  const [{ user }] = useStateValue();
  const location = useLocation();
  const queryTab = new URLSearchParams(location.search).get("tab");
  const [activeTab, setActiveTab] = useState("integrations");
  const [integrationsSub, setIntegrationsSub] = useState("gmail");
  const [impactSub, setImpactSub] = useState("");
  const [superAdminAccess, setSuperAdminAccess] = useState(
    isSuperAdminOwner(user),
  );
  const [collapsedGroups, setCollapsedGroups] = useState({});

  const SETTINGS_TAB_GROUPS = {
    userManagment: "Team & Users",
    coordinators: "Team & Users",
    teamDefaults: "Team & Users",
    superAdmin: "Super Admin",
    impact: "Super Admin",
    teamLeads: "Super Admin",
    leadSource: "Deals & Pipeline",
    lockboxInventory: "Deals & Pipeline",
    taskTemplate: "Deals & Pipeline",
    portalForms: "Deals & Pipeline",
    bulkUpload: "Deals & Pipeline",
    labels: "Contacts",
    contactType: "Contacts",
    emailTemplates: "Communication",
    emailLog: "Communication",
    notifications: "Communication",
    integrations: "Communication",
  };
  const SETTINGS_GROUP_ORDER = [
    "Team & Users",
    "Deals & Pipeline",
    "Contacts",
    "Communication",
    "Super Admin",
  ];

  const tabs = [
    {
      id: "bulkUpload",
      label: "Bulk Upload Deals",
      allowedUserRole: ["Admin", "Supervisor"],
    },
    {
      id: "labels",
      label: "Contact Labels",
      allowedUserRole: ["Admin", "Supervisor"],
    },
    {
      id: "contactType",
      label: "Contact Type",
      allowedUserRole: ["Admin", "Supervisor"],
    },
    {
      id: "emailLog",
      label: "Email Log",
      allowedUserRole: ["Admin", "Supervisor", "Employee"],
    },
    {
      id: "emailTemplates",
      label: "Email Templates",
      allowedUserRole: ["Admin", "Supervisor"],
    },
    {
      id: "integrations",
      label: "Integrations",
      allowedUserRole: ["Admin", "Supervisor", "Employee"],
    },
    {
      id: "leadSource",
      label: "Lead Source",
      allowedUserRole: ["Admin", "Supervisor"],
    },
    {
      id: "lockboxInventory",
      label: "Lockbox Inventory",
      allowedUserRole: ["Admin", "Supervisor", "Employee"],
    },
    {
      id: "notifications",
      label: "Notifications",
      allowedUserRole: ["Admin", "Supervisor"],
    },
    {
      id: "portalForms",
      label: "Portal Forms",
      allowedUserRole: ["Admin"],
    },
    {
      id: "taskTemplate",
      label: "Task Templates",
      allowedUserRole: ["Admin", "Supervisor", "Employee"],
    },
    {
      id: "coordinators",
      label: "Coordinators",
      allowedUserRole: ["Admin", "Supervisor"],
    },
    {
      id: "teamDefaults",
      label: "Team Defaults",
      allowedUserRole: ["Admin", "Supervisor"],
    },
    {
      id: "userManagment",
      label: "User Management",
      allowedUserRole: ["Admin", "Supervisor"],
    },
  ].sort((a, b) => a.label.localeCompare(b.label));

  const userRole = user?.role;

  const filteredTabs = tabs
    .filter(
      (tab) =>
        Array.isArray(tab.allowedUserRole) &&
        tab.allowedUserRole.includes(userRole),
    )
    .concat(
      superAdminAccess ? [{ id: "superAdmin", label: "Super Admin" }] : [],
    )
    .concat(
      isImpactScorecardEnabled({ user }) || isLeadTrackerEnabled({ user })
        ? [{ id: "impact", label: "Impact" }]
        : [],
    )
    .concat(
      isSuperAdminOwner(user)
        ? [{ id: "teamLeads", label: "Team Leads" }]
        : [],
    )
    .sort((a, b) => a.label.localeCompare(b.label));

  useEffect(() => {
    if (!queryTab) return;
    // Old integration tab ids → the Integrations tab + right sub-tab.
    if (LEGACY_SETTINGS_TAB_MAP[queryTab]) {
      setActiveTab("integrations");
      setIntegrationsSub(LEGACY_SETTINGS_TAB_MAP[queryTab]);
      return;
    }
    // Old top-level coaching tab ids → the Impact tab + right sub-tab.
    if (queryTab === "impactScorecard" || queryTab === "leadTracker") {
      setActiveTab("impact");
      setImpactSub(queryTab === "leadTracker" ? "leadTracker" : "scorecard");
      return;
    }
    if (filteredTabs.find((t) => t.id === queryTab)) {
      setActiveTab(queryTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryTab, superAdminAccess]);

  // Super Admin tab visibility: the owner always sees it; granted guests too.
  useEffect(() => {
    let cancelled = false;
    loadBrokerageSettings().then((s) => {
      if (cancelled) return;
      setSuperAdminAccess(
        isSuperAdminOwner(user) || (s.allowedUserIds || []).includes(user?.id),
      );
    });
    return () => {
      cancelled = true;
    };
  }, [user]);

  return (
    <div className="h-screen flex flex-col bg-slate-50">
      <PageHeader title="Settings" />

      <div className="flex-1 min-h-0 flex flex-col lg:flex-row overflow-hidden">
        <nav
          className="
            flex-shrink-0
            flex flex-row lg:flex-col gap-1
            overflow-x-auto lg:overflow-x-visible lg:overflow-y-auto
            border-b lg:border-b-0 lg:border-r border-black/10
            bg-white
            px-3 py-2 lg:px-3 lg:py-4
            lg:w-[220px]
          "
        >
          {SETTINGS_GROUP_ORDER.map((groupName) => {
            const groupTabs = filteredTabs.filter(
              (t) =>
                (SETTINGS_TAB_GROUPS[t.id] || "Communication") === groupName,
            );
            if (groupTabs.length === 0) return null;
            const collapsed = collapsedGroups[groupName];
            return (
              <div key={groupName} className="contents">
                <button
                  type="button"
                  onClick={() =>
                    setCollapsedGroups((c) => ({
                      ...c,
                      [groupName]: !c[groupName],
                    }))
                  }
                  className="hidden lg:flex items-center gap-1.5 px-4 pt-3 pb-1 text-xs font-semibold text-slate-500 hover:text-slate-700"
                >
                  <ChevronDown
                    size={13}
                    className={`transition-transform ${
                      collapsed ? "-rotate-90" : ""
                    }`}
                  />
                  {groupName}
                </button>
                {!collapsed &&
                  groupTabs.map((tab) => {
                    const isActive = activeTab === tab.id;
                    return (
                      <button
                        key={tab.id}
                        type="button"
                        onClick={() => setActiveTab(tab.id)}
                        className={`
                          group relative text-left whitespace-nowrap
                          text-sm font-medium
                          px-4 py-2.5 lg:pl-7 rounded-md flex-shrink-0
                          transition-colors
                          ${
                            isActive
                              ? "bg-slate-50 text-linkColor"
                              : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                          }
                        `}
                      >
                        <span
                          className={`
                            hidden lg:block
                            absolute left-0 top-1/2 -translate-y-1/2
                            w-[3px] rounded-r-md
                            bg-themeColor
                            transition-all duration-200
                            ${isActive ? "h-[60%]" : "h-0 group-hover:h-[40%]"}
                          `}
                        />
                        {tab.label}
                      </button>
                    );
                  })}
              </div>
            );
          })}
        </nav>

        <div className="settings-dense flex-1 min-h-0 overflow-y-auto p-3 lg:p-4">
          {activeTab === "superAdmin" ? (
            <SuperAdminTab />
          ) : activeTab === "teamLeads" ? (
            <TeamLeadsTab />
          ) : activeTab === "impact" ? (
            <ImpactTab initialSub={impactSub} />
          ) : activeTab === "contactType" ? (
            <ContactType />
          ) : activeTab === "leadSource" ? (
            <LeadSourcePage />
          ) : activeTab === "userManagment" ? (
            <MainUserManagement />
          ) : activeTab === "taskTemplate" ? (
            <MainTaskTemplate />
          ) : activeTab === "coordinators" ? (
            <CoordinatorsTab />
          ) : activeTab === "integrations" ? (
            <IntegrationsTab initialSub={integrationsSub} />
          ) : activeTab === "lockboxInventory" ? (
            <MainLockboxInventory />
          ) : activeTab === "emailTemplates" ? (
            <EmailTemplatesTab />
          ) : activeTab === "notifications" ? (
            <NotificationsTab />
          ) : activeTab === "portalForms" ? (
            <PortalFormsEditor />
          ) : activeTab === "labels" ? (
            <LabelsTab />
          ) : activeTab === "bulkUpload" ? (
            <BulkUploadTab />
          ) : activeTab === "teamDefaults" ? (
            <TeamDefaultsTab />
          ) : (
            <EmailLog />
          )}
        </div>
      </div>
    </div>
  );
}

export default MainSettings;
